/**
 * Web shell — application entry point.
 *
 * Mounts the React root, imports the shared design-system theme, and wraps
 * the application in the UI provider stack (head management, reactive
 * favicon, and dark/light/system theme toggling) plus the shared
 * {@link AuthProvider} for authentication state management.
 *
 * This is the host micro-frontend that owns auth, global layout, and
 * acts as the catch-all for unmatched routes.
 *
 * @module main
 */

import '@learnai/tools/prototypes';
import '@learnai/tools/temporal-polyfill';
import '@web/styles/global.css';

import { AuthProvider } from '@learnai/api/auth-provider';
import { QueryProvider } from '@learnai/api/query-provider';
import { logos } from '@learnai/ui/components/brand/logo';
import { Toaster } from '@learnai/ui/components/ui/sonner';
import { getAppVersion } from '@learnai/ui/lib/version';
import { FaviconProvider, HeadContent } from '@learnai/ui/providers/head';
import { LogoProvider } from '@learnai/ui/providers/logo';
import { ThemeProvider } from '@learnai/ui/providers/theme';
import { App } from '@web/app';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------

const { version, shortSha, isReleaseBuild } = getAppVersion();
if (isReleaseBuild) {
	console.info(`LearnAI shell v${version} (${shortSha})`);
}

const root = document.getElementById('root');

if (!root) {
	throw new Error('Missing #root element in index.html');
}

createRoot(root).render(
	<StrictMode>
		<HeadContent title="LearnAI" description="AI-powered education platform" />
		<FaviconProvider>
			<ThemeProvider>
				<LogoProvider logos={logos}>
					<AuthProvider>
						<QueryProvider>
							<App />
							<Toaster richColors />
						</QueryProvider>
					</AuthProvider>
				</LogoProvider>
			</ThemeProvider>
		</FaviconProvider>
	</StrictMode>,
);
